import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Users } from "lucide-react";

import PaymentSummary from "./PaymentSummary";
import PaymentMethods from "./PaymentMethods";
import PaymentNotes from "./PaymentNotes";
import CustomerSelector from "./CustomerSelector";
import PaymentActions from "./PaymentActions";

import { useReceivableTable } from "../../table/hooks/useReceivableTable";
import { useApplyPayment } from "../hooks/useApplyPayment";

import {
  RootState,
  AppDispatch,
} from "../../../../store/store";

import {
  setPaymentDate,
  setNotes,
  setPayments,
} from "../store/receivablePayment.slice";

interface Props {
  receivableId?: number;
}

const PaymentForm = ({ receivableId }: Props) => {
  const dispatch: AppDispatch = useDispatch();

  const { fetchPaymentSummary } = useReceivableTable();

  const {
    applyPayment,
    cancelPayment,
  } = useApplyPayment();

  /**
   * 🔥 leer todo desde redux
   */
  const {
    summary,
    paymentDate,
    notes,
    payments,
  } = useSelector(
    (state: RootState) => state.receivablePayment
  );

  const isContextMode = !!receivableId;

  /**
   * 🔥 cargar cuenta desde backend → redux
   */
  useEffect(() => {
    if (!receivableId) return;

    fetchPaymentSummary(receivableId);
  }, [receivableId]);

  /**
   * 🔥 modo manual:
   * si no viene receivableId y no hay summary,
   * primero seleccionar cliente
   */
  if (!isContextMode && !summary) {
    return <CustomerSelector />;
  }

  /**
   * 🔥 submit centralizado
   */
  const handleSubmit = async () => {
    await applyPayment();
  };

  return (
    <div>
      {/* 🔹 ENCABEZADO */}
      <div className="flex items-center gap-3 bg-success/5 px-6 py-5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-success text-white">
          <Users className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-semibold text-black dark:text-white">
            Cobrar a Clientes
          </h2>
          <p className="text-sm text-body dark:text-bodydark2">
            Registra el cobro de una cuenta por cobrar
          </p>
        </div>
      </div>

      <div className="divide-y divide-stroke dark:divide-strokedark">
        {/* 🔹 RESUMEN + FECHA */}
        {summary && (
          <PaymentSummary
            data={summary}
            paymentDate={paymentDate}
            onPaymentDateChange={(value) =>
              dispatch(setPaymentDate(value))
            }
          />
        )}

        {/* 🔹 MÉTODOS */}
        <PaymentMethods
          items={payments}
          setItems={(value) =>
            dispatch(
              setPayments(
                typeof value === "function"
                  ? value(payments)
                  : value
              )
            )
          }
          balance={summary?.balance || 0}
        />

        {/* 🔹 NOTAS */}
        <PaymentNotes
          value={notes}
          onChange={(value) =>
            dispatch(setNotes(value))
          }
        />
      </div>

      {/* 🔹 ACTIONS */}
      <PaymentActions
        onSubmit={handleSubmit}
        onCancel={cancelPayment}
        isValid={payments.length > 0}
        isSubmitting={false}
      />
    </div>
  );
};

export default PaymentForm;